// src/components/features/integrations/PixelSelectionModal.jsx
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter,
  ModalClose,
} from '@/components/ui/modal';
import { PixelService } from '@/services/pixel.service';

export function PixelSelectionModal({
  open,
  onOpenChange,
  onSelect,
  pixels = [],
}) {
  const [selectedPixelId, setSelectedPixelId] = useState(null);

  // Fetch pixel details
  const { data: pixelsData, isLoading } = useQuery({
    queryKey: ['pixels', { ids: pixels }],
    queryFn: () => PixelService.getPixels({ connectedPixels: pixels }),
    enabled: open && pixels.length > 0,
  });

  const handleSelect = () => {
    if (!selectedPixelId) return;
    onSelect(selectedPixelId);
    onOpenChange(false);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Select a Pixel</ModalTitle>
          <ModalDescription>
            Choose which pixel you want to connect with this integration.
          </ModalDescription>
        </ModalHeader>

        <div className="sl-modal-content py-4">
          {isLoading ? (
            <div className="sl-loading-state flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="sl-pixel-list space-y-3">
              {pixelsData?.pixels.map((pixel) => (
                <label
                  key={pixel.id}
                  className={`sl-pixel-option flex cursor-pointer items-center gap-3 rounded-lg border p-4 ${
                    selectedPixelId === pixel.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                  <input
                    type="radio"
                    name="pixel"
                    value={pixel.id}
                    checked={selectedPixelId === pixel.id}
                    onChange={(e) => setSelectedPixelId(e.target.value)}
                    className="sl-pixel-radio h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="sl-pixel-info">
                    <div className="sl-pixel-id font-mono text-sm font-medium text-gray-900">
                      {pixel.id}
                    </div>
                    <div className="sl-pixel-status text-xs text-gray-500">
                      {pixel.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <ModalFooter>
          <ModalClose asChild>
            <button className="sl-cancel-btn inline-flex items-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
              Cancel
            </button>
          </ModalClose>
          <button
            onClick={handleSelect}
            disabled={!selectedPixelId}
            className="sl-confirm-btn inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50">
            Continue
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
